// src/pages/PrivacyPolicy.jsx
import React, { useMemo } from "react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  const updatedAt = "16 janvier 2026";
  const companyName = "GoShop";
  const appName = "GoShop";
  const websiteUrl = "https://goshopforall.com";
  const contactEmail = "protectiondedonneesgs@gmail.com";

  const sections = useMemo(
    () => [
      { id: "intro", title: "Aperçu" },
      { id: "controller", title: "Responsable" },
      { id: "data", title: "Données collectées" },
      { id: "use", title: "Utilisation" },
      { id: "legal", title: "Fondements" },
      { id: "share", title: "Partage" },
      { id: "payments", title: "Paiements" },
      { id: "cookies", title: "Cookies & stockage local" },
      { id: "security", title: "Sécurité" },
      { id: "retention", title: "Conservation" },
      { id: "rights", title: "Vos droits" },
      { id: "third", title: "Services tiers" },
      { id: "kids", title: "Enfants" },
      { id: "changes", title: "Modifications" },
      { id: "contact", title: "Contact" },
    ],
    []
  );

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="pp-wrap">
      {/* Top bar (Alibaba-like) */}
      <div className="pp-topbar">
        <div className="pp-container">
          <div className="pp-top-left">
            <span className="pp-badge">Politique</span>
            <span className="pp-title">Politique de confidentialité</span>
          </div>

          <div className="pp-top-right">
            <span className="pp-muted">
              Mise à jour : <strong>{updatedAt}</strong>
            </span>
            <a className="pp-btn" href={`mailto:${contactEmail}`}>
              Contacter
            </a>
          </div>
        </div>
      </div>

      {/* Hero */}
      <header className="pp-hero">
        <div className="pp-container">
          <div className="pp-hero-card">
            <div className="pp-hero-left">
              <h1>
                Politique de confidentialité — <span>{companyName}</span>
              </h1>
              <p>
                Cette page explique comment <strong>{companyName}</strong> collecte,
                utilise, partage et protège vos informations lorsque vous utilisez{" "}
                <strong>{appName}</strong> et/ou notre site{" "}
                <strong>{websiteUrl}</strong>.
              </p>

              <div className="pp-hero-meta">
                <div className="pp-meta-item">
                  <div className="pp-meta-k">Applicable à</div>
                  <div className="pp-meta-v">
                    Application mobile + site web
                  </div>
                </div>
                <div className="pp-meta-item">
                  <div className="pp-meta-k">Contact</div>
                  <div className="pp-meta-v">{contactEmail}</div>
                </div>
              </div>

              <div className="pp-hero-actions">
                <button className="pp-primary" onClick={() => jumpTo("rights")}>
                  Vos droits
                </button>
                <button className="pp-ghost" onClick={() => jumpTo("data")}>
                  Données collectées
                </button>
              </div>
            </div>

            <div className="pp-hero-right">
              <div className="pp-info-tile">
                <div className="pp-info-title">Résumé rapide</div>
                <ul className="pp-info-list">
                  <li>Nous ne vendons pas vos données.</li>
                  <li>Nous partageons avec les vendeurs pour livrer vos commandes.</li>
                  <li>Nous utilisons des mesures de sécurité raisonnables.</li>
                  <li>Vous pouvez demander l’accès/suppression via email.</li>
                </ul>
                <div className="pp-mini-links">
                  <Link to="/terms">Conditions d’utilisation</Link>
                  <span>·</span>
                  <Link to="/contact">Support</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pp-container pp-main">
        {/* Sidebar (Table of contents) */}
        <aside className="pp-aside">
          <div className="pp-toc">
            <div className="pp-toc-title">Sommaire</div>
            <div className="pp-toc-list">
              {sections.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="pp-toc-link"
                  onClick={() => jumpTo(s.id)}
                >
                  {s.title}
                </button>
              ))}
            </div>

            <div className="pp-card pp-tip">
              <div className="pp-tip-title">Pour Google Play</div>
              <p className="pp-tip-text">
                Cette page peut être utilisée comme URL de “Politique de confidentialité”
                dans la Play Console.
              </p>
              <a className="pp-tip-cta" href={websiteUrl} target="_blank" rel="noreferrer">
                Ouvrir le site
              </a>
            </div>
          </div>
        </aside>

        {/* Document */}
        <section className="pp-doc">
          <article className="pp-paper">
            <Section id="intro" title="Aperçu">
              <p>
                La présente politique décrit les catégories de données que nous collectons,
                y compris les contenus générés par les utilisateurs (messages, produits, interactions),
                nos finalités de traitement, les partages éventuels, et vos droits.
              </p>
            </Section>

            <Section id="controller" title="1) Responsable du traitement">
              <p>
                Responsable : <strong>{companyName}</strong>
                <br />
                Contact :{" "}
                <a className="pp-link" href={`mailto:${contactEmail}`}>
                  {contactEmail}
                </a>
              </p>
            </Section>

            <Section id="data" title="2) Informations que nous collectons">
              <ul className="pp-ul">
                <li>
                  <strong>Informations fournies</strong> : nom/prénom, téléphone, email,
                  adresse de livraison, messages via WhatsApp/formulaires, informations
                  vendeur (boutique, contact, etc.).
                </li>
                <li>
                  <strong>Données de commande</strong> : produits, quantités, montant,
                  statut, vendeur concerné, historique.
                </li>
                <li>
                  <strong>Données techniques</strong> : type d’appareil, système d’exploitation,
                  identifiants uniques (Firebase), logs techniques, performances, interactions
                  avec l’application.
                </li>
                <li>
                  <strong>Contenu utilisateur</strong> : messages envoyés via le chat, échanges
                  entre utilisateurs et vendeurs.
                </li>
              </ul>

              <div className="pp-callout">
                <div className="pp-callout-title">Important</div>
                <div className="pp-callout-text">
                  Évitez de partager des informations sensibles dans les messages publics.
                </div>
              </div>
            </Section>

            <Section id="use" title="3) Comment nous utilisons vos informations">
              <ul className="pp-ul">
                <li>Traiter commandes, demandes et livraisons.</li>
                <li>Vous contacter (WhatsApp / téléphone / email).</li>
                <li>Améliorer la recherche, l’UX et la sécurité.</li>
                <li>Prévenir la fraude et protéger utilisateurs/vendeurs.</li>
                <li>Support et gestion des réclamations.</li>
              </ul>
            </Section>

            <Section id="moderation" title="3 bis) Modération et signalement">
              <p>
                Les utilisateurs peuvent interagir entre eux via des messages et des contenus
                (produits, boutiques, discussions).
              </p>
              <ul className="pp-ul">
                <li>
                  Vous pouvez signaler un contenu inapproprié ou un comportement abusif en
                  contactant notre support à l’adresse suivante :
                  <a className="pp-link" href={`mailto:${contactEmail}`}>
                    {" "}{contactEmail}
                  </a>
                </li>
                <li>
                  Tous les signalements sont examinés manuellement par notre équipe.
                </li>
                <li>
                  Nous nous réservons le droit de supprimer tout contenu ou compte ne respectant
                  pas nos conditions.
                </li>
              </ul>
            </Section>

            <Section id="legal" title="4) Fondements (explication simple)">
              <ul className="pp-ul">
                <li>
                  <strong>Exécution du service</strong> : livrer, traiter une commande,
                  gérer les échanges.
                </li>
                <li>
                  <strong>Intérêt légitime</strong> : sécurité, prévention fraude,
                  amélioration.
                </li>
                <li>
                  <strong>Obligations légales</strong> : si requis (litiges, conformité).
                </li>
              </ul>
            </Section>

            <Section id="share" title="5) Partage des informations">
              <p>Nous ne vendons pas vos données. Partages possibles :</p>
              <ul className="pp-ul">
                <li>
                  <strong>Avec les vendeurs</strong> : pour exécuter la commande
                  (livraison, disponibilité, contact).
                </li>
                <li>
                  <strong>Prestataires techniques</strong> : hébergement, sécurité,
                  analyses/diagnostics si nécessaire.
                </li>
                <li>
                  <strong>Obligations légales</strong> : si la loi l’exige.
                </li>
              </ul>
            </Section>

            <Section id="payments" title="6) Paiements">
              <p>
                Les paiements peuvent être traités par des prestataires externes
                (ex : Mobile Money / passerelles). Ces prestataires appliquent leurs
                propres politiques. Nous partageons uniquement les informations nécessaires
                à la confirmation de transaction.
              </p>
            </Section>

            <Section id="cookies" title="7) Cookies & stockage local">
              <p>
                Nous pouvons utiliser des cookies/stockage local pour mémoriser certaines
                préférences (ex : panier) et améliorer les performances. Vous pouvez les
                désactiver dans votre navigateur.
              </p>
            </Section>

            <Section id="security" title="8) Sécurité">
              <p>
                Nous mettons en place des mesures techniques et organisationnelles
                raisonnables pour protéger vos données. Aucun système n’étant sécurisé
                à 100%, la prudence reste recommandée.
              </p>
            </Section>

            <Section id="retention" title="9) Durée de conservation">
              <p>
                Nous conservons vos données uniquement le temps nécessaire pour : commandes,
                support, sécurité et obligations légales.
              </p>
            </Section>

            <Section id="rights" title="10) Vos droits">
              <ul className="pp-ul">
                <li>Accès, correction ou suppression (si possible).</li>
                <li>Opposition à certains traitements.</li>
                <li>Informations sur l’usage de vos données.</li>
                <li>
                  Vous pouvez demander la suppression complète de votre compte et de vos données.
                </li>
              </ul>
              <div className="pp-contact-box">
                <div>
                  <div className="pp-contact-k">Exercer vos droits</div>
                  <div className="pp-contact-v">
                    Écrivez-nous à{" "}
                    <a className="pp-link" href={`mailto:${contactEmail}`}>
                      {contactEmail}
                    </a>
                  </div>
                </div>
                <a className="pp-primary pp-primary-sm" href={`mailto:${contactEmail}`}>
                  Envoyer un email
                </a>
              </div>
            </Section>

            <Section id="third" title="11) Services tiers (WhatsApp, etc.)">
              <p>
                Des liens vers WhatsApp ou d’autres services tiers peuvent exister.
                Nous ne contrôlons pas leurs politiques. Nous recommandons de les consulter.
              </p>
              <p>
                Nous utilisons des services tiers tels que Firebase (Google) pour l’authentification,
                le stockage des données, les notifications et les performances.
              </p>
            </Section>

            <Section id="kids" title="12) Données des enfants">
              <p>
                {companyName} n’a pas pour objectif de collecter des données personnelles
                d’enfants. Si vous pensez qu’un enfant a transmis des données, contactez-nous.
              </p>
            </Section>

            <Section id="changes" title="13) Modifications">
              <p>
                Nous pouvons mettre à jour cette politique. La date de mise à jour est
                affichée en haut de la page.
              </p>
            </Section>

            <Section id="contact" title="14) Contact">
              <p>
                Email :{" "}
                <a className="pp-link" href={`mailto:${contactEmail}`}>
                  {contactEmail}
                </a>
                <br />
                Site :{" "}
                <a className="pp-link" href={websiteUrl} target="_blank" rel="noreferrer">
                  {websiteUrl}
                </a>
              </p>
            </Section>

            <footer className="pp-footer-note">
              © {new Date().getFullYear()} {companyName}. Tous droits réservés.
            </footer>
          </article>
        </section>
      </main>

      {/* Styles (Alibaba-ish, moderne) */}
      <style>{css}</style>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="pp-section">
      <div className="pp-section-head">
        <h2 className="pp-h2">{title}</h2>
        <a className="pp-anchor" href={`#${id}`} title="Lien direct">
          #
        </a>
      </div>
      <div className="pp-section-body">{children}</div>
    </section>
  );
}

const css = `
.pp-wrap{
  background: radial-gradient(1200px 600px at 20% -10%, rgba(255,143,0,.18), transparent 60%),
              radial-gradient(900px 500px at 90% 0%, rgba(30,64,175,.18), transparent 55%),
              #f6f7fb;
  min-height: 100vh;
}

/* Container */
.pp-container{ max-width: 1120px; margin: 0 auto; padding: 0 14px; }

/* Topbar */
.pp-topbar{
  position: sticky;
  top: 0;
  z-index: 30;
  background: rgba(255,255,255,.78);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(15,23,42,.08);
}
.pp-topbar .pp-container{
  display:flex; align-items:center; justify-content:space-between;
  padding: 10px 14px;
}
.pp-top-left{ display:flex; align-items:center; gap:10px; min-width: 0; }
.pp-badge{
  background: rgba(255,143,0,.16);
  color: #8a4b00;
  border: 1px solid rgba(255,143,0,.25);
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 12px;
}
.pp-title{
  font-weight: 900;
  color: #0f172a;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pp-top-right{
  display:flex; align-items:center; gap:10px;
}
.pp-muted{ color:#475569; font-size: 12px; }
.pp-btn{
  display:inline-flex; align-items:center; justify-content:center;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid rgba(15,23,42,.12);
  background: #fff;
  color:#0f172a;
  font-weight: 800;
  font-size: 12px;
  text-decoration:none;
}
.pp-btn:hover{ border-color: rgba(15,23,42,.22); transform: translateY(-1px); }

/* Hero */
.pp-hero{ padding: 18px 0 10px; }
.pp-hero-card{
  display:grid;
  grid-template-columns: 1.35fr .65fr;
  gap: 14px;
  border-radius: 22px;
  padding: 14px;
  background: linear-gradient(180deg, #ffffff, #fbfbff);
  border: 1px solid rgba(15,23,42,.08);
  box-shadow: 0 18px 40px rgba(15,23,42,.08);
}
.pp-hero-left{ padding: 10px 10px 12px; }
.pp-hero-left h1{
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 950;
  color:#0f172a;
  letter-spacing: -.2px;
}
.pp-hero-left h1 span{ color: #ff8f00; }
.pp-hero-left p{
  margin: 0 0 12px;
  color: #334155;
  font-size: 14px;
}
.pp-hero-meta{
  display:grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 8px;
}
.pp-meta-item{
  background: #f8fafc;
  border: 1px solid rgba(15,23,42,.06);
  border-radius: 16px;
  padding: 10px 12px;
}
.pp-meta-k{ color:#64748b; font-size: 12px; font-weight: 800; margin-bottom: 3px; }
.pp-meta-v{ color:#0f172a; font-size: 13px; font-weight: 900; }
.pp-hero-actions{ display:flex; gap:10px; margin-top: 12px; flex-wrap: wrap; }

.pp-primary{
  border: 0;
  background: linear-gradient(180deg, #ff9f1a, #ff8f00);
  color: #111827;
  font-weight: 950;
  padding: 10px 14px;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(255,143,0,.25);
}
.pp-primary:hover{ transform: translateY(-1px); }
.pp-primary-sm{ padding: 8px 12px; font-size: 13px; text-decoration:none; display:inline-flex; align-items:center; }
.pp-ghost{
  border: 1px solid rgba(15,23,42,.12);
  background: #fff;
  color: #0f172a;
  font-weight: 900;
  padding: 10px 14px;
  border-radius: 999px;
  cursor: pointer;
}
.pp-ghost:hover{ border-color: rgba(15,23,42,.22); transform: translateY(-1px); }

/* Right tile */
.pp-hero-right{ padding: 10px; }
.pp-info-tile{
  height: 100%;
  background:
    radial-gradient(350px 180px at 70% 0%, rgba(255,143,0,.18), transparent 65%),
    #0b1226;
  border-radius: 18px;
  padding: 14px 14px 12px;
  color: #e5e7eb;
  border: 1px solid rgba(255,255,255,.08);
}
.pp-info-title{ font-weight: 950; font-size: 14px; margin-bottom: 8px; }
.pp-info-list{ margin: 0; padding-left: 16px; color: #dbeafe; font-size: 13px; line-height: 1.6; }
.pp-mini-links{
  display:flex; gap:8px; align-items:center; margin-top: 10px;
  font-size: 12px; color: rgba(226,232,240,.85);
}
.pp-mini-links a{ color: #fff; text-decoration: none; font-weight: 800; }
.pp-mini-links a:hover{ text-decoration: underline; }

/* Main layout */
.pp-main{
  display:grid;
  grid-template-columns: 320px 1fr;
  gap: 14px;
  padding: 10px 14px 30px;
}

/* Aside / TOC */
.pp-aside{ position: relative; }
.pp-toc{
  position: sticky;
  top: 62px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(15,23,42,.08);
  box-shadow: 0 16px 34px rgba(15,23,42,.06);
  padding: 12px;
}
.pp-toc-title{ font-weight: 950; color:#0f172a; margin-bottom: 8px; }
.pp-toc-list{ display:flex; flex-direction:column; gap: 6px; }
.pp-toc-link{
  text-align:left;
  border: 1px solid rgba(15,23,42,.08);
  background: #f8fafc;
  color:#0f172a;
  border-radius: 12px;
  padding: 9px 10px;
  font-weight: 900;
  font-size: 13px;
  cursor:pointer;
}
.pp-toc-link:hover{ background: #fff; border-color: rgba(15,23,42,.14); transform: translateY(-1px); }

.pp-card{
  margin-top: 12px;
  border-radius: 16px;
  border: 1px solid rgba(15,23,42,.08);
  background: #fff;
  padding: 12px;
}
.pp-tip{
  background: linear-gradient(180deg, #fff7ed, #ffffff);
}
.pp-tip-title{ font-weight: 950; color:#7c2d12; margin-bottom: 6px; }
.pp-tip-text{ margin: 0 0 10px; color:#334155; font-size: 13px; }
.pp-tip-cta{
  display:inline-flex;
  text-decoration:none;
  font-weight: 950;
  color:#0f172a;
  border: 1px solid rgba(15,23,42,.12);
  padding: 8px 10px;
  border-radius: 12px;
  background: #fff;
}

/* Doc */
.pp-doc{ min-width: 0; }
.pp-paper{
  background:#fff;
  border-radius: 18px;
  border: 1px solid rgba(15,23,42,.08);
  box-shadow: 0 18px 40px rgba(15,23,42,.06);
  padding: 14px 16px;
}
.pp-section{ padding: 10px 2px; scroll-margin-top: 80px; }
.pp-section + .pp-section{ border-top: 1px dashed rgba(15,23,42,.12); }
.pp-section-head{ display:flex; align-items:center; justify-content:space-between; gap:10px; }
.pp-h2{
  margin: 0;
  font-size: 16px;
  font-weight: 950;
  color:#0f172a;
}
.pp-anchor{
  text-decoration:none;
  color:#94a3b8;
  border: 1px solid rgba(148,163,184,.35);
  border-radius: 10px;
  padding: 2px 8px;
  font-weight: 950;
}
.pp-anchor:hover{ color:#0f172a; border-color: rgba(15,23,42,.18); }
.pp-section-body{ margin-top: 6px; color:#0f172a; }
.pp-link{ color:#1e40af; font-weight: 900; text-decoration: none; }
.pp-link:hover{ text-decoration: underline; }
.pp-ul{ margin: 8px 0 0; padding-left: 18px; color:#0f172a; }
.pp-ul li{ margin: 6px 0; color:#334155; }

.pp-callout{
  margin-top: 12px;
  border-radius: 16px;
  padding: 12px;
  background: linear-gradient(180deg, rgba(30,64,175,.06), rgba(255,143,0,.06));
  border: 1px solid rgba(15,23,42,.08);
}
.pp-callout-title{ font-weight: 950; color:#0f172a; margin-bottom: 4px; }
.pp-callout-text{ color:#334155; font-size: 13px; }

.pp-contact-box{
  margin-top: 10px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 10px;
  padding: 12px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid rgba(15,23,42,.08);
}
.pp-contact-k{ color:#64748b; font-size: 12px; font-weight: 950; }
.pp-contact-v{ color:#0f172a; font-size: 13px; font-weight: 900; }

.pp-footer-note{
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid rgba(15,23,42,.08);
  color:#64748b;
  font-size: 12px;
  font-weight: 800;
}

/* Responsive */
@media (max-width: 980px){
  .pp-hero-card{ grid-template-columns: 1fr; }
  .pp-main{ grid-template-columns: 1fr; }
  .pp-aside{ order: 2; }
  .pp-toc{ position: relative; top: 0; }
}
@media (max-width: 520px){
  .pp-hero-left h1{ font-size: 18px; }
  .pp-hero-meta{ grid-template-columns: 1fr; }
  .pp-top-right .pp-muted{ display:none; }
}
`;

